import React, { useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme/theme.ts";
import Layout from "./common/components/Layout/layout.tsx";
import AppRoutes from "./Screens/index.tsx";
import "./App.css";
import { SignalRContext } from "./config/signalR.ts";
import { ChatConnString } from "./config/api.ts";
import axios from "axios";
import useAuth0 from "./common/Auth0Provider/use-auth0.ts";
import { useDispatch, useSelector } from "react-redux";
import SplashScreen from "@common/SplashScreen";
import { fetchTermsAndConditions, verifyUser } from "@store/userSlice.tsx";
import { AppDispatch } from "@store/store.tsx";
import CallHandler from "./Screens/CallHandler.tsx";
import { setWSConnected, setWSDisconnected } from "@store/configSlice.tsx";
import NotificationRequest from "@common/Notification/notificationRequest.tsx";
import UserDeactivatedScreen from "@common/UserDeactivatedScreen/userDeactivatedScreen.tsx";
import InactivityLogout from "@common/InactivityLogout";
import LogoutSplashScreen from "@common/LogoutSplashScreen/logoutSplashScreen.tsx";
import { v4 as uuidv4 } from 'uuid';
import TermsAndConditions from "@common/TermsAndConditions/termsAndConditions.tsx";
import NetworkStatusManager from "@common/components/Network/NetworkStatusManager.tsx";

const App: React.FC = () => {
    const { loginWithRedirect, isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0();
    const [isActive, setIsActive] = useState(false)
    const { user, termsAndConditions } = useSelector((state: any) => state.user)
    const [userDeactivated, setUserDeactivated] = useState(false);
    const [showLogoutSpashScreen, setShowLogoutSpashScreen] = useState(false);
    const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const error = params.get("error");
        const errorDescription = params.get("error_description");
        const isDeactivated =
            error === "access_denied" &&
            errorDescription?.includes("email address is blocked");
        if (isDeactivated) {
            setUserDeactivated(true);
            return;
        }
        if (window.location.pathname.includes("/logout")) {
            setShowLogoutSpashScreen(true);
            return;
        }
        const redirectUri = params.get("redirect_uri");
        if (!isAuthenticated && !isLoading && !window.location.href.toLowerCase().includes('mapp')) {
            loginWithRedirect({
                appState: { returnTo: redirectUri ?? "/" },
            });
        } else if (isAuthenticated) {
            setIsActive(true)
            axios.interceptors.request.use(
                async (config) => {
                    const token = await getAccessTokenSilently()
                    if (token) {
                        config.headers.Authorization = `Bearer ${token}`;
                    } else {
                        console.warn("No access token available");
                    }
                    config.headers["X-Correlation-ID"] = uuidv4()
                    return config;
                },
                (error) => {
                    if (error instanceof Error) {
                        return Promise.reject(error);
                    }
                    return Promise.reject(new Error(typeof error === 'string' ? error : JSON.stringify(error)));
                }
            );
            axios.interceptors.response.use(
                (response) => response,
                async (error) => {
                    if (error.response?.status === 401) {
                        console.warn("Unauthorized! Logging out...");
                        logout({ logoutParams: { returnTo: window.location.origin }, });
                    }
                    return Promise.reject(error instanceof Error ? error : new Error(JSON.stringify(error)));
                }
            );
        }
    }, [isAuthenticated, isLoading]);

    React.useEffect(() => {
        if (isActive)
            dispatch(verifyUser())
    }, [isActive])

    useEffect(() => {
        if (user) {
            dispatch(fetchTermsAndConditions());
        }
    }, [user])

    useEffect(() => {
        if (user && termsAndConditions?.version) {
            if (!user?.isTermsAndConditionsAccepted || user?.termsAndConditionsAcceptedVersion !== termsAndConditions?.version) {
                setShowTermsAndConditions(true);
            }
        }
    }, [user, termsAndConditions])
    if (userDeactivated) {
        return <UserDeactivatedScreen />;
    }

    if (showLogoutSpashScreen) {
        return <LogoutSplashScreen />;
    }

    return (
        <>
            <NetworkStatusManager />
            {isAuthenticated && isActive && user ? (
                <SignalRContext.Provider
                    accessTokenFactory={async () => await getAccessTokenSilently()}
                    url={ChatConnString}
                    automaticReconnect={true}
                    onReconnect={() => {
                        dispatch(setWSConnected())
                    }}
                    onError={async () => {
                        dispatch(setWSDisconnected())
                    }}
                    onOpen={() => {
                        dispatch(setWSConnected())
                    }}
                    onClosed={() => {
                        dispatch(setWSDisconnected())
                    }}
                >
                    <CallHandler>
                        <ThemeProvider theme={theme}>
                            {
                                window.location.href.toLowerCase().includes('mapp') ?
                                    <Router basename={'mApp'}>
                                        <AppRoutes />
                                    </Router>
                                    :
                                    <Router>
                                        <NotificationRequest />
                                        <InactivityLogout />
                                        <Layout>
                                            <TermsAndConditions setShowTermsAndConditions={setShowTermsAndConditions} showTermsAndConditions={showTermsAndConditions} />
                                            <AppRoutes />
                                        </Layout>
                                    </Router>
                            }
                        </ThemeProvider>
                    </CallHandler>
                </SignalRContext.Provider>
            ) :
                <SplashScreen />
            }
        </>
    );
};
export default App;
